import classNames from 'classnames'
import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { Fieldset } from 'primereact/fieldset'
import { ToggleButton } from 'primereact/togglebutton'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { ModuleForm } from './ModuleForm'
import { claims } from '../../../../../claims'
import {
  API_APPRENTICESHIP_MODULE,
  API_APPRENTICESHIP_MODULES,
} from '../../../../../constants'
import { ApprenticeshipModule } from '../../../../../model/apprenticeshipModule.model'
import { RequestMethod } from '../../../../../model/enums/enums'
import {
  addModule,
  addModules,
  removeModules,
  updateModule,
} from '../../../../../redux/basic/grades/apprenticeship-module/moduleActions'
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks'
import { handleRequest } from '../../../../../services/api/base/handleRequest'
import { hasAnyRequiredClaim } from '../../../../../util/validation/hasClaim'
import { InactiveRowStyle } from '../../../../util/data-table/row-class/InactiveRow'
import { ModuleTypeTag } from '../utils/ModuleTypeTag'

const defaultModule = {
  apprenticeship_module_id: 0,
  module_key: null,
  id_apprenticeship_module_type: null,
  version: null,
  bivo: null,
  year_of_release: null,
}

export const ApprenticeshipModuleModule = (): JSX.Element => {
  const userClaims = useAppSelector((state) => state.claims)
  const apprenticeshipModules = useAppSelector(
    (state) => state.apprenticeshipModules
  )

  const requiredClaimsAdd: string[] = [
    claims.gradeModule.main,
    claims.gradeModule.apprenticeshipModules.main,
    claims.gradeModule.apprenticeshipModules.create,
  ]
  const requiredClaimsEdit = [
    claims.gradeModule.main,
    claims.gradeModule.apprenticeshipModules.main,
    claims.gradeModule.apprenticeshipModules.edit,
  ]

  const [editDialog, setEditDialog] = useState<boolean>(false)

  const dispatcher = useAppDispatch()

  useEffect(() => {
    const loadModules = async (): Promise<void> => {
      const mods = await handleRequest<ApprenticeshipModule[]>(
        API_APPRENTICESHIP_MODULES(),
        RequestMethod.GET
      )
      if (mods?.data) {
        // we want to clear and add all modules
        dispatcher(removeModules())
        dispatcher(addModules(mods.data))
      }
    }

    loadModules()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addForm = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: defaultModule,
  })
  const editForm = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: defaultModule,
  })

  const handleModuleAdd = async (moduleToAdd: any) => {
    const formCopy = { ...moduleToAdd }
    if (moduleToAdd.year_of_release === '') {
      formCopy.year_of_release = null
    }
    const result = await handleRequest<ApprenticeshipModule>(
      API_APPRENTICESHIP_MODULES(),
      RequestMethod.POST,
      formCopy
    )
    if (!result.error && result.data) {
      dispatcher(addModule(result.data))
      addForm.reset(defaultModule)
    }
  }

  const handleModuleEdit = async (moduleToEdit: any) => {
    if (!moduleToEdit) {
      return
    }
    const formCopy = { ...moduleToEdit }
    if (formCopy.year_of_release === '') {
      formCopy.year_of_release = null
    }
    const result = await handleRequest<ApprenticeshipModule>(
      API_APPRENTICESHIP_MODULE(formCopy.apprenticeship_module_id),
      RequestMethod.PUT,
      formCopy
    )
    if (result.error) {
      console.warn(
        `[Apprenticeship Module] Could not edit module ${moduleToEdit.apprenticeship_module_id}.`
      )
      return
    }

    if (result.data?.apprenticeship_module_id) {
      dispatcher(updateModule(result.data))
    }

    if (editDialog) {
      editForm.reset(defaultModule)
      setEditDialog(false)
    }
  }

  /**
   * change module status from active to inactive and vice versa and set to inactive if not defined.
   * @param module
   */
  const handleActivity = async (module: ApprenticeshipModule) => {
    const body = { ...module }
    body.is_active =
      module.is_active === undefined || module.is_active === 1 ? 0 : 1
    await handleModuleEdit(body)
  }

  const activeBody = (module: ApprenticeshipModule) => {
    const showButton = hasAnyRequiredClaim(userClaims, requiredClaimsEdit)
    const isActive = module?.is_active === 1
    if (showButton) {
      return (
        <ToggleButton
          checked={isActive}
          type={'button'}
          offIcon={'fa-solid fa-xmark'}
          onIcon={'fa-solid fa-check'}
          onLabel={t<string>('core.active')}
          offLabel={t<string>('core.inactive')}
          className={classNames(
            { 'icon-red ': !isActive },
            ' p-button-raised min-width-120 '
          )}
          onChange={() => handleActivity(module)}
        />
      )
    }

    return (
      <span className={isActive ? ' fa-solid fa-check' : 'fa-solid fa-xmark'} />
    )
  }

  const getTableHeight = () => {
    if (hasAnyRequiredClaim(userClaims, requiredClaimsAdd)) {
      return 'calc(100vh - 420px)'
    }
    return 'calc(100vh - 180px)'
  }

  const [showAll, setShowAll] = useState<boolean>(false)

  return (
    <div className={'card pr-2 w-full'}>
      {hasAnyRequiredClaim(userClaims, requiredClaimsAdd) && (
        <Fieldset legend={`${t<string>('grade_module.module.add')}`}>
          <FormProvider {...addForm}>
            <form
              className={
                'flex flex-wrap inline-grid w-full  justify-content-center align-items-center'
              }
              onSubmit={addForm.handleSubmit(handleModuleAdd)}
            >
              <ModuleForm />
              <div className={'col flex mt-auto'}>
                <Button type={'submit'} label={t<string>('core.add')} />
              </div>
            </form>
          </FormProvider>
        </Fieldset>
      )}

      <div
        className={
          'inline-flex mt-2 mb-2 w-full justify-content-between align-items-center'
        }
      >
        <div />
        <div>
          <Dropdown
            value={showAll}
            className={'min-width-240'}
            options={[
              { value: true, label: t<string>('core.show_all') },
              {
                value: false,
                label: t<string>('core.show_active'),
              },
            ]}
            onChange={() => setShowAll(!showAll)}
          />
        </div>
      </div>

      <div style={{ overflow: 'auto', height: getTableHeight() }}>
        <DataTable
          value={
            showAll
              ? apprenticeshipModules
              : apprenticeshipModules.filter((am) => am.is_active === 1)
          }
          style={{ maxWidth: '100%' }}
          rowHover
          dataKey={'apprenticeship_module_id'}
          rowClassName={InactiveRowStyle}
          emptyMessage={t<string>('core.empty_message')}
          scrollHeight={'flex'}
          scrollable
          stripedRows
          globalFilterFields={[
            'module_key',
            'description',
            'bivo',
            'version',
            'release_year',
          ]}
          sortField={'module_key'}
          sortMode={'single'}
          filterDisplay={'menu'}
        >
          <Column
            header={t<string>('grade_module.module.key')}
            field={'module_key'}
            sortable
          />
          <Column
            header={t<string>('grade_module.module.description')}
            field={'description'}
            sortable
          />
          <Column
            field={'id_apprenticeship_module_type'}
            header={t<string>('grade_module.module.type.name')}
            body={(e: ApprenticeshipModule) => (
              <ModuleTypeTag moduleType={e?.id_apprenticeship_module_type} />
            )}
            sortable
          />
          <Column
            field={'bivo'}
            header={t<string>('core.education.bivo')}
            sortable
          />
          <Column
            field={'year_of_release'}
            header={t<string>('grade_module.module.release_year')}
            sortable
          />
          <Column
            field={'version'}
            header={t<string>('grade_module.module.version')}
            sortable
          />
          <Column
            field={'is_active'}
            header={t<string>('core.is_active')}
            sortable
            body={activeBody}
          />
          {hasAnyRequiredClaim(userClaims, requiredClaimsEdit) && (
            <Column
              body={(module) => (
                <Button
                  className={'p-button-text'}
                  icon={'fa-solid fa-pen'}
                  type={'button'}
                  onClick={() => {
                    editForm.reset(module)
                    setEditDialog(true)
                  }}
                />
              )}
            />
          )}
        </DataTable>
      </div>
      <Dialog
        visible={editDialog}
        closeOnEscape={true}
        resizable={true}
        style={{ width: '90%' }}
        maximizable={true}
        onHide={() => {
          setEditDialog(false)
        }}
        header={`${t<string>('core.edit')}`}
      >
        <FormProvider {...editForm}>
          <form
            className={'flex flex-wrap inline-grid w-full pb-2 mb-2'}
            onSubmit={editForm.handleSubmit(handleModuleEdit)}
          >
            <ModuleForm />
            <div className={'col mt-1'}>
              <Button
                type={'submit'}
                className={'mt-3'}
                label={t<string>('core.save')}
              />
            </div>
          </form>
        </FormProvider>
      </Dialog>
    </div>
  )
}